import { useEffect, useMemo, useState } from 'react';
import { createHttpClient } from '@shared/api/httpClient';
import type { ClientWorkflowState } from '@shared/types/api';
import { recordsFromResponse, text, type ApiRecord } from '../pages/packageUi';

type GenerationState = {
  visible: boolean;
  title: string;
  message: string;
};

type EvidenceWorkflowStatusResponse = {
  workflowActive?: boolean;
  workflowState?: ClientWorkflowState;
};

const FINAL_PACKAGE_PENDING_STATUSES = new Set([
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_REQUESTED',
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_IN_PROGRESS',
  'COMPLETED_EVIDENCE_PACKAGE_UPLOADED_TO_INTAKE',
  'SUBMITTED_FOR_FINAL_AUDITOR_PACKAGE',
  'FINAL_AUDITOR_PACKAGE_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_IN_PROGRESS',
  'FINAL_ASSESSOR_REVIEW_PENDING',
]);

const FINAL_PACKAGE_SUCCESS_STATUSES = new Set([
  'AUDITOR_PACKAGE_READY',
  'FINAL_AUDITOR_PACKAGE_READY',
  'FINAL_PACKAGE_READY',
]);

const FINAL_PACKAGE_FAILURE_MARKERS = [
  'FAILED',
  'FAILURE',
  'ERROR',
  'DECLINED',
  'DENIED',
  'REJECTED',
  'CANCELLED',
  'CANCELED',
  'TIMED_OUT',
];

const FINAL_PACKAGE_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function upper(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function packageStatuses(pkg: ApiRecord): string[] {
  return ['workspaceStatus', 'packageStatus', 'status', 'packageLifecycleStatus', 'jobStatus', 'displayStatus']
    .map((key) => upper(text(pkg, key)))
    .filter(Boolean);
}

function hasMarker(statuses: string[], markers: string[]): boolean {
  return statuses.some((status) => markers.some((marker) => status.includes(marker)));
}

function packageGroupKey(pkg: ApiRecord): string {
  return text(pkg, 'jobId', 'sourceJobId', 'workflowJobId', 'packageId');
}

function packageUpdatedAt(pkg: ApiRecord): number {
  const raw = text(pkg, 'updatedAt', 'createdAt', 'completedAt', 'requestedAt');
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function finalPackageGenerationRunning(packages: ApiRecord[]): boolean {
  const relevant = packages
    .filter((pkg) => {
      const statuses = packageStatuses(pkg);
      return upper(text(pkg, 'packageType')).includes('FINAL_AUDITOR')
        || statuses.some((status) => FINAL_PACKAGE_PENDING_STATUSES.has(status))
        || statuses.some((status) => FINAL_PACKAGE_SUCCESS_STATUSES.has(status))
        || hasMarker(statuses, FINAL_PACKAGE_FAILURE_MARKERS);
    })
    .sort((left, right) => packageUpdatedAt(right) - packageUpdatedAt(left));

  const latest = relevant[0];
  if (!latest) return false;
  const latestUpdatedAt = packageUpdatedAt(latest);
  if (latestUpdatedAt && Date.now() - latestUpdatedAt > FINAL_PACKAGE_PENDING_MAX_AGE_MS) return false;

  const groupKey = packageGroupKey(latest);
  const group = groupKey
    ? relevant.filter((pkg) => packageGroupKey(pkg) === groupKey)
    : [latest];
  const statuses = group.flatMap(packageStatuses);

  if (statuses.some((status) => FINAL_PACKAGE_SUCCESS_STATUSES.has(status))) return false;
  if (hasMarker(statuses, FINAL_PACKAGE_FAILURE_MARKERS)) return false;
  return statuses.some((status) => FINAL_PACKAGE_PENDING_STATUSES.has(status));
}

export function deriveGenerationState(
  workflowResponse: EvidenceWorkflowStatusResponse,
  packages: ApiRecord[],
): GenerationState {
  const workflowState = workflowResponse.workflowState;
  const finalPackageRunning = finalPackageGenerationRunning(packages);
  const evidenceRunning = Boolean(workflowState?.evidenceRunning);
  const fullWorkflowRunning = Boolean(workflowState?.fullWorkflowRunning);
  const workflowRunning = evidenceRunning || fullWorkflowRunning || Boolean(workflowResponse.workflowActive);

  if (!finalPackageRunning && !workflowRunning) {
    return { visible: false, title: '', message: '' };
  }

  if (finalPackageRunning) {
    return {
      visible: true,
      title: 'Final auditor package generation is in progress',
      message: 'The final package workflow is still running. You may continue using the portal. This notice will clear after the workflow reports success or failure.',
    };
  }

  if (fullWorkflowRunning) {
    return {
      visible: true,
      title: 'Readiness workflow is in progress',
      message: 'The active workflow is still running for this AWS account. You may continue using the portal. This notice will clear after the workflow reports success or failure.',
    };
  }

  return {
    visible: true,
    title: 'Evidence package generation is in progress',
    message: 'The evidence workflow is still running for this AWS account. You may continue using the portal. This notice will clear after the workflow reports success or failure.',
  };
}

export default function PackageGenerationBanner() {
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [state, setState] = useState<GenerationState>({ visible: false, title: '', message: '' });

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    async function refresh() {
      try {
        const workflowResponse = await api.get<EvidenceWorkflowStatusResponse>('/client/evidence-workflow');
        let packages: ApiRecord[] = [];
        try {
          const packageResponse = await api.get<unknown>('/client/packages?limit=100&page=1');
          packages = recordsFromResponse(packageResponse, ['packages', 'items']);
        } catch {
          // Final-package status is supplemental. The authoritative account workflow state
          // must still be allowed to clear or display the banner by itself.
        }
        if (!active) return;
        setState(deriveGenerationState(workflowResponse, packages));
      } catch {
        // Fail closed visually. A stale status banner is more misleading than no banner
        // when the authoritative account-scoped workflow endpoint is unavailable.
        if (active) setState({ visible: false, title: '', message: '' });
      } finally {
        if (active) timer = window.setTimeout(refresh, 30_000);
      }
    }

    void refresh();
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [api]);

  if (!state.visible) return null;
  return (
    <section className="card package-generation-banner" role="status" aria-live="polite">
      <div className="package-generation-banner-indicator" aria-hidden="true" />
      <div>
        <h3>{state.title}</h3>
        <p>{state.message}</p>
      </div>
    </section>
  );
}
